import { useState, useEffect } from 'react';
import { api } from '../services/api';

function LinkGenerator() {
  const [agents, setAgents] = useState([]);
  const [formData, setFormData] = useState({
    ticketId: '',
    agentId: '',
    lockAgent: false,
    customerEmail: '',
    customerName: '',
    expiresInDays: 30,
    meetingType: 'phone',
    duration: 30,
  });
  const [generatedLink, setGeneratedLink] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await api.getAgents();
      setAgents(data);
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const result = await api.generateLink({
        ticketId: formData.ticketId || null,
        agentId: formData.agentId || null,
        lockedAgentId: (formData.lockAgent && formData.agentId) ? formData.agentId : null,
        customerEmail: formData.customerEmail || null,
        customerName: formData.customerName || null,
        expiresInDays: parseInt(formData.expiresInDays),
        meetingType: formData.meetingType,
        duration: parseInt(formData.duration),
      });

      setGeneratedLink(result);
      setLoading(false);

      // Reset form
      setFormData({
        ticketId: '',
        agentId: '',
        lockAgent: false,
        customerEmail: '',
        customerName: '',
        expiresInDays: 30,
        meetingType: 'phone',
        duration: 30,
      });
    } catch (error) {
      console.error('Error generating link:', error);
      alert('Failed to generate link');
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink.schedulingUrl);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="max-w-2xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Generate Booking Link</h2>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <p className="text-gray-600 mb-6">
          Create a unique booking link for scheduling appointments. Optionally link it to a
          Halo PSA ticket for automatic integration, or leave blank to create a standalone link.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ticket ID (Optional)
            </label>
            <input
              type="text"
              value={formData.ticketId}
              onChange={(e) => setFormData({ ...formData, ticketId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="12345"
            />
            <p className="mt-1 text-sm text-gray-500">
              Leave blank to create a standalone link (no Halo integration)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agent (Optional)
            </label>
            <select
              value={formData.agentId}
              onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Any Available Agent</option>
              {agents.map((agent) => (
                <option key={agent.agentId} value={agent.agentId}>
                  {agent.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Leave empty to allow customer to choose from all available agents
            </p>

            {formData.agentId && (
              <div className="mt-3 flex items-center">
                <input
                  type="checkbox"
                  id="lockAgent"
                  checked={formData.lockAgent}
                  onChange={(e) => setFormData({ ...formData, lockAgent: e.target.checked })}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <label htmlFor="lockAgent" className="ml-2 text-sm text-gray-700">
                  <strong>Force this agent</strong> - Customer cannot select a different agent
                </label>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Type *
            </label>
            <select
              required
              value={formData.meetingType}
              onChange={(e) => setFormData({ ...formData, meetingType: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="phone">Phone Call (Agent will call customer)</option>
              <option value="teams">Microsoft Teams Meeting (Auto-generated)</option>
              <option value="on-site">On-Site Support (Agent visits customer)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Select how the meeting will be conducted
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appointment Duration *
            </label>
            <select
              required
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              How long each appointment slot should be
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Name (Optional)
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Email (Optional)
            </label>
            <input
              type="email"
              value={formData.customerEmail}
              onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Link Expiration (Days)
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={formData.expiresInDays}
              onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {loading ? 'Generating...' : 'Generate Link'}
          </button>
        </form>
      </div>

      {/* Generated Link Display */}
      {generatedLink && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-green-900 mb-2">
                Link Generated Successfully!
              </h3>

              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600 mb-2">Booking Link:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-gray-50 px-3 py-2 rounded border border-gray-200 break-all">
                    {generatedLink.schedulingUrl}
                  </code>
                  <button
                    onClick={copyToClipboard}
                    className="flex-shrink-0 bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="text-sm text-green-800 space-y-1">
                <p><strong>Link ID:</strong> {generatedLink.linkId}</p>
                {generatedLink.ticketId ? (
                  <p><strong>Ticket:</strong> #{generatedLink.ticketId}</p>
                ) : (
                  <p><strong>Type:</strong> Standalone (no ticket)</p>
                )}
                <p><strong>Expires:</strong> {new Date(generatedLink.expiresAt).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LinkGenerator;
